/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineComponent, ref, reactive, watchEffect } from "vue";
import { mapState } from "vuex";
import { AxiosResponse } from "axios";
import { accessToken } from "@/services/utils/helper.util";
import { api } from "@/dto/api.endpoint.dto";
import { globals } from "@/main";

type FormType = {
  code: string[]; // Define the type of 'code' array
};

export default defineComponent({
  name: "c-media-from-computer",

  props: {
    isArray: { type: Boolean, default: false },
    isUndo: { type: Boolean, default: false },
  },

  emits: ["selectedMedia"],

  setup(props, { emit }) {
    const isUploading = ref(false);
    const errorMessage = ref("");
    const uploadPercentage = ref<number>(0);
    const files = ref<File[]>([]);
    const fileUploaded = ref<File[]>([]);
    const folder = ref("other");
    const mediaType = ref(1);

    const listSelectedMedia = ref<string[]>([]);
    const serverMedia = reactive<any[]>([]);

    const selectedIndex = ref(-1);
    const uploadIndexIndex = ref(-1);
    const responseBody = ref("");
    const beforeUploadIndex = ref(-1);

    const mediaDirectory = mapState("mediaDirectory", ["mediaDirectory"]);

    const fileInput = ref<HTMLInputElement | null>(null);

    const onChange = () => {
      const input = fileInput.value;
      if (input && input.files) {
        const newFiles = Array.from(input.files);
        for (const newFile of newFiles) {
          const isDuplicate = files.value.some((existingFile) => {
            return existingFile.name === newFile.name;
          });

          if (!isDuplicate) {
            files.value.unshift(newFile);
          }
        }

        if (newFiles.length === 1) {
          beforeUploadIndex.value = 0;
        }
      } else {
        // Handle the case when input.files is null or undefined
        console.error("No files selected.");
      }
    };

    const beforeUpload = async () => {
      if (files.value.length === 0) {
        uploadPercentage.value = 0;
        isUploading.value = false;
        uploadIndexIndex.value = -1;
        return;
      }

      isUploading.value = true;
      uploadIndexIndex.value = beforeUploadIndex.value;

      const response = await onUpload(files.value[beforeUploadIndex.value]);

      if (typeof response === "string") {
        uploadPercentage.value = 0;
        errorMessage.value = response;
      } else {
        if (response?.status === 201) {
          serverMedia.push(response?.data?.data);
          await fileUploaded.value.push(files.value[0]);
          files.value.splice(beforeUploadIndex.value, 1);
        } else {
          uploadPercentage.value = 0;
          errorMessage.value = response?.data?.message || "Upload failed.";
        }
      }

      isUploading.value = false;
      uploadIndexIndex.value = -1;
    };

    const onUpload = async (
      file: File
    ): Promise<AxiosResponse<any> | string> => {
      const formData = new FormData();
      formData.append("file", file);

      const token = await accessToken;
      const params =
        "?folder=" +
        folder.value +
        "&type=" +
        mediaType.value +
        "&accept-language=en:en";

      const header = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + token.value,
        },
        onUploadProgress: function (progressEvent: ProgressEvent) {
          const percentage = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          );
          uploadPercentage.value = percentage;
        },
      };

      try {
        const response: any = await globals.$axios.post(
          api.uploadMedia + params,
          formData,
          header
        );
        if (!props.isArray) {
          form.value.code = response?.data?.data?.key;
          emit("selectedMedia", response?.data?.data?.key);
        }
        return response;
      } catch (errors: any) {
        return errors?.response?.data || "Upload failed.";
      }
    };

    const dragover = (e: DragEvent) => {
      e.preventDefault();
      // this.isDragging = true;
    };

    const dragleave = () => {
      // this.isDragging = false;
    };

    const validExtensions = ["jpg", "jpeg", "png"];

    const drop = (e: DragEvent) => {
      e.preventDefault();

      if (e.dataTransfer) {
        const files = e.dataTransfer.files;
        const validFiles: File[] = [];
        const invalidFiles: File[] = [];

        // Validate each file
        for (const file of files) {
          const fileExtension = file.name.split(".").pop()?.toLowerCase();
          if (fileExtension && validExtensions.includes(fileExtension)) {
            validFiles.push(file);
          } else {
            invalidFiles.push(file);
          }
        }

        // Create a new DataTransfer object to manage files
        const dataTransfer = new DataTransfer();

        // Add valid files to the DataTransfer object
        validFiles.forEach((file) => dataTransfer.items.add(file));

        // Update the file input with valid files
        if (fileInput.value !== null) {
          fileInput.value.files = dataTransfer.files; // Use the DataTransfer object
          onChange();
        }

        // Log invalid files
        if (invalidFiles.length > 0) {
          console.error(
            "Invalid file types removed:",
            invalidFiles.map((file) => file.name)
          );
          // Optionally, show a message to the user here
        }
      }
    };

    const removeDraftFile = (fileIndex: number) => {
      files.value.splice(fileIndex, 1);
      if (fileInput.value !== null) {
        fileInput.value.value = "";
      }
    };

    const fileToPath = (file: File) => {
      return window.URL.createObjectURL(file);
    };

    const resetBody = () => {
      uploadPercentage.value = 0;
      files.value = [];
      folder.value = "other";
      mediaType.value = 1;
      if (fileInput.value !== null) {
        fileInput.value.value = "";
      }
    };

    const onChangeMedia = () => {
      emit("selectedMedia", form.value.code);
    };

    const submitUpload = async () => {
      await beforeUpload();
      if (responseBody.value) {
        emit("selectedMedia", responseBody.value);
      }
    };

    watchEffect(() => {
      // $route.fullPath
    });

    const form = ref<FormType>({
      code: [],
    });

    return {
      isUploading,
      errorMessage,
      uploadPercentage,
      files,
      fileInput,
      fileUploaded,
      folder,
      mediaType,
      listSelectedMedia,
      serverMedia,
      selectedIndex,
      uploadIndexIndex,
      responseBody,
      beforeUploadIndex,
      mediaDirectory,
      onChange,
      beforeUpload,
      onUpload,
      dragover,
      dragleave,
      drop,
      removeDraftFile,
      fileToPath,
      resetBody,
      onChangeMedia,
      submitUpload,
      form,
    };
  },
});
